if (!window.ProductSetForm) {
  const getCartAddUrl = () => {
    const base = window.MinimogSettings?.routes?.cart_add_url || "/cart/add";
    return base.endsWith(".js") ? base : `${base}.js`;
  };

  const isVariantOutOfStock = (variant) => {
    if (!variant) return true;
    return variant.available === false;
  };

  const isSizeInputDisabled = (input) =>
    !input || input.disabled || input.classList.contains("disabled");

  // Modal-only controller for set/wrapper products. The wrapper product itself is
  // selected and added to cart through the normal variant-picker + product-form
  // pipeline (identical to a normal PDP). This class only powers the optional
  // "Want Different Sizes for Top and Pants" modal, which adds either the wrapper
  // variant (same size) or two component line items (different sizes).
  class ProductSetForm {
    constructor(productForm) {
      this.productForm = productForm;
      this.form = productForm.querySelector("form");
      this.errorWrapper = productForm.querySelector(".m-product-form-message");
      this.productInfo = productForm.closest(".m-main-product--info");
      this.setPicker = this.productInfo?.querySelector("product-set-picker");
      this.cart = document.querySelector("m-cart-drawer");

      if (!this.setPicker) return;

      this.modalContent = this.setPicker.querySelector("[data-set-size-modal]");
      this.modalHome = this.setPicker.querySelector("[data-set-size-modal-home]");
      this.modalSubmitButton = this.modalContent?.querySelector("[data-set-modal-add-to-cart]");
      this.openModalBtn = this.setPicker.querySelector("[data-open-set-size-modal]");
      this.kurtaSizePosition = Number(this.setPicker.dataset.kurtaSizePosition) || 0;
      this.trouserSizePosition = Number(this.setPicker.dataset.trouserSizePosition) || 0;
      this.setSizePosition = Number(this.setPicker.dataset.setSizePosition) || 0;

      const setVariantScript = this.setPicker.querySelector("[data-set-product-variants]");
      this.setVariantData = setVariantScript ? JSON.parse(setVariantScript.textContent) : [];

      const metaScript = this.setPicker.querySelector("[data-set-product-meta]");
      try {
        this.productMeta = metaScript ? JSON.parse(metaScript.textContent) : {};
      } catch {
        this.productMeta = {};
      }

      this.components = ["kurta", "trouser"];
      this.variantData = {};
      this.componentEls = {};
      this.components.forEach((key) => {
        const componentEl = this.setPicker.querySelector(`[data-set-component="${key}"]`);
        if (componentEl) this.componentEls[key] = componentEl;

        const script = this.setPicker.querySelector(`[data-set-component-variants="${key}"]`);
        if (script) {
          this.variantData[key] = JSON.parse(script.textContent);
        }
      });

      this.sizeModal = null;
      this.init();
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;

      if (!this.setPicker || !this.modalContent) return;

      if (this.openModalBtn) {
        this.styleOpenModalButton();
        this.openModalBtn.addEventListener("click", (event) => {
          event.preventDefault();
          this.pushBannerClickEvent();
          this.openSizeModal();
        });
      }

      this.modalContent.addEventListener("change", (event) => {
        if (isSizeInputDisabled(event.target)) return;
        if (event.target.closest("[data-set-component]")) {
          this.onComponentSizeChange();
        }
      });

      this.modalContent.addEventListener("click", (event) => {
        if (event.target.closest("[data-set-modal-add-to-cart]")) {
          event.preventDefault();
          this.onModalSubmitHandler(event);
          return;
        }
        if (event.target.closest(".product-set-size-modal__close")) {
          event.preventDefault();
          this.sizeModal?.close();
          return;
        }
        const input = event.target.closest(".m-product-option__input");
        if (input && !isSizeInputDisabled(input) && input.closest("[data-set-component]")) {
          requestAnimationFrame(() => {
            if (!isSizeInputDisabled(input)) this.onComponentSizeChange();
          });
        }
      });

      this.boundModalClosed = this.onSizeModalClosed.bind(this);
      window.MinimogEvents.subscribe("ON_MODAL_CLOSED", this.boundModalClosed);
    }

    styleOpenModalButton() {
      if (!this.openModalBtn) return;
      const isDesktop = this.isDesktopInline();
      Object.assign(this.openModalBtn.style, {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        marginTop: isDesktop ? "0.5rem" : "0.25rem",
        padding: "0.75rem 0.7rem",
        border: "1px solid #d9d9d9",
        borderRadius: "4px",
        background: "#d9d9d9",
        fontSize: isDesktop ? "14px" : "12px",
        fontWeight: "500",
        fontFamily: "var(--font-stack-body)",
        lineHeight: isDesktop ? "18px" : "16px",
        textTransform: "none",
        letterSpacing: "normal",
        color: "#1B1212",
        cursor: "pointer",
        textDecoration: "none",
      });
    }

    pushBannerClickEvent() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "split_size_banner_click",
        ecommerce: {
          items: [
            {
              item_id: this.productMeta.product?.item_id || "",
              item_name: this.productMeta.product?.item_name || "",
              item_brand: this.productMeta.product?.item_brand || "",
              item_category: this.productMeta.product?.item_category || "",
              item_variant: "",
            },
          ],
        },
      });
    }

    isDesktopInline() {
      return window.matchMedia("(min-width: 768px)").matches;
    }

    getComponentElement(componentKey) {
      return this.componentEls[componentKey] || null;
    }

    getSizeValueForComponent(componentKey) {
      const position =
        componentKey === "kurta" ? this.kurtaSizePosition : this.trouserSizePosition;
      if (!position) return null;

      const componentEl = this.getComponentElement(componentKey);
      if (!componentEl) return null;

      const optionEl = componentEl.querySelector(`[data-set-option-position="${position}"]`);
      if (!optionEl) return null;

      const checkedRadio = optionEl.querySelector('input[type="radio"]:checked');
      const select = optionEl.querySelector("select[data-set-component-input], select");

      if (checkedRadio) return checkedRadio.value;
      if (select && select.value) return select.value;
      return null;
    }

    clearStaleSelectedAttrs(container) {
      if (!container) return;
      container.querySelectorAll("[data-selected]").forEach((el) => el.removeAttribute("data-selected"));
    }

    clearRadioSelection(container) {
      if (!container) return;
      this.clearStaleSelectedAttrs(container);
      container.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = false;
        input.removeAttribute("checked");
      });
    }

    clearModalSelection() {
      this.components.forEach((componentKey) => {
        const componentEl = this.getComponentElement(componentKey);
        if (!componentEl) return;
        this.clearRadioSelection(componentEl);
        componentEl.querySelectorAll("select[data-set-component-input], select").forEach((select) => {
          select.selectedIndex = -1;
        });
      });
    }

    getSelectedOptionsByPosition(componentKey) {
      const componentEl = this.getComponentElement(componentKey);
      if (!componentEl) return {};

      const selectedByPosition = {};
      componentEl.querySelectorAll("[data-set-option-position]").forEach((optionEl) => {
        const position = Number(optionEl.dataset.setOptionPosition);
        if (!position) return;

        const checkedRadio = optionEl.querySelector('input[type="radio"]:checked');
        const select = optionEl.querySelector("select[data-set-component-input], select");

        if (checkedRadio) {
          selectedByPosition[position] = checkedRadio.value;
        } else if (select && select.value) {
          selectedByPosition[position] = select.value;
        } else {
          selectedByPosition[position] = null;
        }
      });

      return selectedByPosition;
    }

    findVariantFromModal(componentKey) {
      const variants = this.variantData[componentKey] || [];
      const selectedByPosition = this.getSelectedOptionsByPosition(componentKey);
      const positions = Object.keys(selectedByPosition).map(Number);

      if (!positions.length || positions.some((position) => !selectedByPosition[position])) {
        return null;
      }

      const matches = variants.filter((variant) =>
        positions.every((position) => variant.options[position - 1] === selectedByPosition[position])
      );

      return matches.find((variant) => variant.available) || matches[0] || null;
    }

    findSetVariantForSize(size) {
      const variants = this.setVariantData || [];
      if (!size || !variants.length || !this.setSizePosition) return null;
      const idx = this.setSizePosition - 1;
      const matches = variants.filter((variant) => variant.options[idx] === size);
      return matches.find((variant) => variant.available) || matches[0] || null;
    }

    updateSelectedLabels() {
      this.components.forEach((componentKey) => {
        const componentEl = this.getComponentElement(componentKey);
        if (!componentEl) return;

        componentEl.querySelectorAll("[data-set-option-position]").forEach((optionEl) => {
          const label = optionEl.querySelector("[data-set-selected-label]");
          const checkedRadio = optionEl.querySelector('input[type="radio"]:checked');
          const select = optionEl.querySelector("select");

          if (label) {
            if (checkedRadio) {
              label.textContent = checkedRadio.value;
            } else if (select) {
              label.textContent = select.value;
            }
          }
        });
      });
    }

    updateModalAddToCartButton() {
      if (!this.modalSubmitButton) return;

      const kurtaVariant = this.findVariantFromModal("kurta");
      const trouserVariant = this.findVariantFromModal("trouser");
      const buttonText = this.modalSubmitButton.querySelector(".m-add-to-cart--text");
      const { MinimogStrings } = window;

      if (!kurtaVariant || !trouserVariant) {
        this.modalSubmitButton.setAttribute("disabled", "disabled");
        return;
      }

      if (isVariantOutOfStock(kurtaVariant) || isVariantOutOfStock(trouserVariant)) {
        this.modalSubmitButton.setAttribute("disabled", "disabled");
        if (buttonText) buttonText.textContent = MinimogStrings.soldOut;
        return;
      }

      this.modalSubmitButton.removeAttribute("disabled");
      if (buttonText) buttonText.textContent = MinimogStrings.addToCart;
    }

    onComponentSizeChange() {
      this.components.forEach((key) => {
        const componentEl = this.getComponentElement(key);
        if (componentEl) this.clearStaleSelectedAttrs(componentEl);
      });

      this.updateSelectedLabels();
      this.updateModalAddToCartButton();
    }

    openSizeModal() {
      if (!this.modalContent || !window.MinimogTheme?.Modal) return;

      if (!this.sizeModal) {
        this.sizeModal = new MinimogTheme.Modal();
      }

      this.clearModalSelection();

      const colorScheme = this.modalContent.dataset.colorScheme || "";
      this.sizeModal.appendChild(this.modalContent);
      this.sizeModal.setWidth(this.isDesktopInline() ? "450px" : "100%");
      this.sizeModal.setSizes(`product-set-size-modal m-set-size-modal m-gradient ${colorScheme}`);

      if (this.sizeModal.modal) {
        this.sizeModal.modal.classList.add("m-set-size-modal-wrapper");
      }

      this.modalContent.style.padding = "1.5rem 1.75rem 1.75rem";
      this.modalContent.style.borderRadius = "16px";
      this.modalContent.style.boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.25)";
      this.modalContent.style.background = "rgb(var(--color-background))";

      const footer = this.modalContent.querySelector(".product-set-size-modal__footer");
      if (footer) {
        footer.style.display = "flex";
        footer.style.flexDirection = "column";
        footer.style.alignItems = "stretch";
      }
      if (this.modalSubmitButton) {
        this.modalSubmitButton.style.width = "100%";
        this.modalSubmitButton.style.display = "flex";
        this.modalSubmitButton.style.justifyContent = "center";
        this.modalSubmitButton.style.alignItems = "center";
      }

      const header = this.modalContent.querySelector(".product-set-size-modal__header");
      if (header && this.isDesktopInline()) {
        header.style.marginBottom = "1.75rem";
      }

      this.modalContent.querySelectorAll(".option-label--title").forEach((l) => {
        l.style.fontWeight = "600";
      });

      this.updateSelectedLabels();
      this.updateModalAddToCartButton();
      this.sizeModal.open();

      this.modalContent.querySelectorAll(".m-product-option--node__label").forEach((l) => {
        l.style.width = "40px";
        l.style.height = "40px";
        l.style.minWidth = "40px";
        l.style.maxWidth = "40px";
        l.style.minHeight = "40px";
        l.style.borderRadius = "50%";
      });
    }

    onSizeModalClosed() {
      if (!this.modalContent || !this.modalHome) return;
      if (this.modalContent.parentElement !== this.modalHome) {
        this.modalHome.appendChild(this.modalContent);
      }
      if (this.sizeModal?.modal) {
        this.sizeModal.modal.classList.remove("m-set-size-modal-wrapper");
      }
    }

    toggleSpinner(show, targetButton) {
      const method = show ? "add" : "remove";
      if (targetButton) {
        targetButton.classList[method]("is-loading");
        return;
      }
      this.productForm.classList[method]("m-spinner-loading");
    }

    onModalSubmitHandler(evt) {
      evt.preventDefault();
      evt.stopPropagation();

      const kurtaVariant = this.findVariantFromModal("kurta");
      const trouserVariant = this.findVariantFromModal("trouser");
      const { MinimogStrings } = window;

      if (!kurtaVariant || !trouserVariant) {
        return window.MinimogTheme.Notification.show({
          target: this.errorWrapper ? this.errorWrapper : document.body,
          method: "appendChild",
          type: "error",
          message: MinimogStrings.selectVariant,
          last: 3000,
          sticky: !this.errorWrapper,
        });
      }

      if (isVariantOutOfStock(kurtaVariant) || isVariantOutOfStock(trouserVariant)) {
        return window.MinimogTheme.Notification.show({
          target: this.errorWrapper ? this.errorWrapper : document.body,
          method: "appendChild",
          type: "error",
          message: MinimogStrings.soldOut,
          last: 3000,
          sticky: !this.errorWrapper,
        });
      }

      const kurtaSize = this.getSizeValueForComponent("kurta");
      const trouserSize = this.getSizeValueForComponent("trouser");

      if (kurtaSize && trouserSize && kurtaSize === trouserSize) {
        const setVariant = this.findSetVariantForSize(kurtaSize);
        if (setVariant) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: "split_size_add_to_cart",
            split_size_session: true,
            ecommerce: {
              items: [
                {
                  item_id: this.productMeta.product?.item_id || "",
                  item_name: this.productMeta.product?.item_name || "",
                  item_brand: this.productMeta.product?.item_brand || "",
                  item_category: this.productMeta.product?.item_category || "",
                  piece_type: "set",
                  price: Number(setVariant.price) / 100,
                  quantity: 1,
                  item_variant: kurtaSize,
                },
              ],
            },
          });
          this.addItemsToCart([{ id: Number(setVariant.id), quantity: 1 }], {
            productVariantId: `${setVariant.id}`,
            spinnerButton: this.modalSubmitButton,
          });
          return;
        }
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "split_size_add_to_cart",
        split_size_session: true,
        ecommerce: {
          items: [
            {
              item_id: this.productMeta.product?.item_id || "",
              item_name: this.productMeta.product?.item_name || "",
              item_brand: this.productMeta.product?.item_brand || "",
              item_category: this.productMeta.product?.item_category || "",
              piece_type: "kurta",
              price: Number(kurtaVariant.price) / 100,
              quantity: 1,
              item_variant: kurtaSize,
            },
            {
              item_id: this.productMeta.product?.item_id || "",
              item_name: this.productMeta.product?.item_name || "",
              item_brand: this.productMeta.product?.item_brand || "",
              item_category: this.productMeta.product?.item_category || "",
              piece_type: "pant",
              price: Number(trouserVariant.price) / 100,
              quantity: 1,
              item_variant: trouserSize,
            },
          ],
        },
      });
      this.addItemsToCart(
        [
          { id: Number(kurtaVariant.id), quantity: 1 },
          { id: Number(trouserVariant.id), quantity: 1 },
        ],
        {
          productVariantId: `${kurtaVariant.id},${trouserVariant.id}`,
          spinnerButton: this.modalSubmitButton,
        }
      );
    }

    addItemsToCart(items, options = {}) {
      const { productVariantId = "", spinnerButton = null } = options;
      const { MinimogSettings, MinimogStrings } = window;

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const payload = { items };

      if (sectionsToBundle.length) {
        payload.sections = sectionsToBundle;
        payload.sections_url = window.location.pathname;
      }

      const config = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      };

      this.toggleSpinner(true, spinnerButton);

      fetch(getCartAddUrl(), config)
        .then((response) => {
          if (!response.ok) {
            return response.text().then((text) => {
              throw new Error(text || `Cart add failed (${response.status})`);
            });
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            document.dispatchEvent(
              new CustomEvent("product-ajax:error", {
                detail: { errorMessage: response.description || response.message },
              })
            );
            return window.MinimogTheme.Notification.show({
              target: this.errorWrapper ? this.errorWrapper : document.body,
              method: "appendChild",
              type: "error",
              message: response.message || response.description,
              last: 3000,
              sticky: !this.errorWrapper,
            });
          }

          if (MinimogSettings.use_ajax_atc) {
            if (this.cart && MinimogSettings.enable_cart_drawer) {
              MinimogTheme.Notification.show({
                target: this.cart.querySelector("m-cart-items"),
                method: "prepend",
                type: "success",
                message: MinimogStrings.itemAdded,
                delay: 400,
              });
              if (typeof handleFloCartBtn === "function") {
                handleFloCartBtn();
              }
            } else {
              window.MinimogTheme.Notification.show({
                target: this.errorWrapper ? this.errorWrapper : document.body,
                method: "appendChild",
                type: "success",
                message: MinimogStrings.itemAdded,
                last: 3000,
                sticky: !this.errorWrapper,
              });
            }

            window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
              cart: response,
              productVariantId,
            });

            document.dispatchEvent(
              new CustomEvent("product-ajax:added", { detail: { product: response } })
            );

            this.sizeModal?.close();
          } else {
            window.location = MinimogSettings.routes.cart;
          }
        })
        .catch((error) => {
          console.error("[product-set-form] Cart add failed:", error);
          window.MinimogTheme.Notification.show({
            target: this.errorWrapper ? this.errorWrapper : document.body,
            method: "appendChild",
            type: "error",
            message: MinimogStrings.error || "Could not add items to cart. Please try again.",
            last: 3000,
            sticky: !this.errorWrapper,
          });
        })
        .finally(() => {
          this.toggleSpinner(false, spinnerButton);
        });
    }
  }

  window.ProductSetForm = ProductSetForm;

  const initProductSetForms = () => {
    document.querySelectorAll('product-form[data-is-product-set="true"].m-product-form--main').forEach((productForm) => {
      if (!productForm.dataset.setFormInitialized) {
        new ProductSetForm(productForm);
        productForm.dataset.setFormInitialized = "true";
      }
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initProductSetForms);
  } else {
    initProductSetForms();
  }
}
